import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HiDotsVertical, HiExternalLink, HiLogout, HiMoon, HiSun } from "react-icons/hi";
import ChatPage from "./ChatPage";
import Login from "../auth/login";
import { ChatProvider, useChat } from "../../components/chat/ChatProvider";
import { NoticeProvider, useNotices } from "../../components/notices/NoticeProvider";
import OfflineBanner from "../../components/ui/OfflineBanner";
import UserAvatar from "../../components/ui/UserAvatar";
import { ChatInstallBanner } from "../../components/ui/InstallGuide";
import { getCachedRole, getUserProfile, isSignedOutError } from "../../controllers/utilityController";
import { CHAT_APP_PATH, useChatManifest } from "../../services/chatApp";
import { isRunningStandalone } from "../../services/pwa";
import { signOut } from "../../services/signOut";
import { ROLES } from "../../constants";

// IRG Chat: the installable, chat-only app at /chat (public/chat-manifest.json). It signs people in by
// itself, so the installed app never has to leave its scope for the ERP login page.

const DASHBOARD_BY_ROLE = {
  [ROLES.ADMIN]: "/admin/dashboard",
  [ROLES.HR]: "/hr/dashboard",
  [ROLES.EMPLOYEE]: "/employee/dashboard",
  [ROLES.MANAGER]: "/employee/dashboard",
};

// Same saved theme as the ERP header toggle (localStorage "darkMode").
const readDarkMode = () => {
  try {
    return JSON.parse(localStorage.getItem("darkMode")) === true;
  } catch {
    return false;
  }
};

function ChatAppBar({ role }) {
  const { me } = useChat();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dark, setDark] = useState(readDarkMode);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    try {
      localStorage.setItem("darkMode", JSON.stringify(dark));
    } catch {
      /* theme just isn't remembered */
    }
  }, [dark]);

  const item = "flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700";

  return (
    <header className="relative flex items-center gap-3 px-3 py-2 shrink-0 bg-white dark:bg-gray-900">
      <img src="/logo192.png" alt="" className="h-8 w-8 rounded-lg" />
      <h1 className="min-w-0 flex-1 truncate text-lg font-semibold text-gray-900 dark:text-gray-100">IRG Chat</h1>
      {me && <UserAvatar name={me.name} src={me.avatarThumbUrl} seed={me.id} size="sm" />}
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        aria-label="Menu"
        className="p-2 rounded-full text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
      >
        <HiDotsVertical className="w-5 h-5" />
      </button>
      {menuOpen && (
        <>
          <button type="button" aria-label="Close menu" className="fixed inset-0 z-[60] cursor-default" onClick={() => setMenuOpen(false)} />
          <div role="menu" className="absolute right-3 top-full z-[61] mt-1 w-56 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
            {/* The ERP opens in the browser: it's no longer an installed app of its own. */}
            <a role="menuitem" href={DASHBOARD_BY_ROLE[role] || "/"} target="_blank" rel="noopener noreferrer" className={item} onClick={() => setMenuOpen(false)}>
              <HiExternalLink className="w-5 h-5 text-gray-400" /> Open the ERP
            </a>
            <button role="menuitem" type="button" className={item} onClick={() => setDark((v) => !v)}>
              {dark ? <HiSun className="w-5 h-5 text-gray-400" /> : <HiMoon className="w-5 h-5 text-gray-400" />}
              {dark ? "Light mode" : "Dark mode"}
            </button>
            <button role="menuitem" type="button" className={`${item} text-red-600 dark:text-red-400`} onClick={() => signOut(CHAT_APP_PATH)}>
              <HiLogout className="w-5 h-5" /> Sign out
            </button>
          </div>
        </>
      )}
    </header>
  );
}

function ChatAppScreen({ role }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { openNotice } = useNotices();
  // ?install=1: sent here by an ERP "Get IRG Chat" button, so show the install card even if dismissed.
  const [installRequested] = useState(() => new URLSearchParams(location.search).get("install") === "1");

  // ?n=<id>: a notice-board push opened the app; show that notice. ?install=1 is dropped from the URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const noticeId = Number(params.get("n"));
    if (!noticeId && !params.has("install")) return;
    if (noticeId) openNotice(noticeId);
    params.delete("n");
    params.delete("install");
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true });
  }, [location.search, location.pathname, navigate, openNotice]);

  return (
    <ChatPage
      appBar={<ChatAppBar role={role} />}
      banner={!isRunningStandalone() && <ChatInstallBanner force={installRequested} />}
    />
  );
}

function ChatApp() {
  useChatManifest();
  const location = useLocation();
  const [session, setSession] = useState({ status: "checking", role: null }); // checking | signed-out | ready

  useEffect(() => {
    document.title = "IRG Chat";
    document.documentElement.classList.toggle("dark", readDarkMode());
  }, []);

  // Checked on open, and again after signing in (the sign-in screen navigates back to /chat). People stay
  // signed in until they sign out themselves or the server rejects the token.
  useEffect(() => {
    if (session.status === "ready") return undefined;
    if (!localStorage.getItem("token")) {
      setSession({ status: "signed-out", role: null });
      return undefined;
    }
    let live = true;
    getUserProfile()
      .then((profile) => live && setSession({ status: "ready", role: profile?.role || null }))
      .catch((err) => {
        if (!live) return;
        // Only a rejected token (401) signs people out. Offline or a server hiccup: open anyway; messages
        // queue until the connection is back.
        setSession(isSignedOutError(err) ? { status: "signed-out", role: null } : { status: "ready", role: getCachedRole() });
      });
    return () => {
      live = false;
    };
  }, [location.key, session.status]);

  if (session.status === "checking") {
    return (
      <div className="flex justify-center items-center min-h-[100dvh] bg-white dark:bg-gray-900">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (session.status === "signed-out") return <Login redirectTo={CHAT_APP_PATH} />;

  return (
    <ChatProvider>
      <NoticeProvider>
        <ChatAppScreen role={session.role} />
        <OfflineBanner />
      </NoticeProvider>
    </ChatProvider>
  );
}

export default ChatApp;
